import { create } from 'zustand'
import { CHAT_ROLE } from '@/constants/conversation.constants'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  source_documents?: any[]
  timestamp?: number
  charts?: any[]
}

export interface Conversation {
  id: string
  title: string
  lastUpdated: number
}

interface ChatState {
  messages: ChatMessage[]
  conversationId: string | null
  conversations: Conversation[]
  setConversationId: (id: string | null) => void
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string
  updateLastMessage: (content: string, metadata?: Partial<Omit<ChatMessage, 'content'>>) => void
  clearMessages: () => void

  updateConversationTitle: (id: string, title: string) => void
  addConversation: (conversation: Omit<Conversation, 'lastUpdated'>) => void
  removeConversation: (id: string) => void
  clearAll: () => void

  loadConversation: (id: string, messages: ChatMessage[]) => void
  errorCode: number | string | null
  setErrorCode: (code: number | string | null) => void
}

const useChatStore = create<ChatState>()((set, get) => ({
  messages: [],
  conversationId: null,
  conversations: [],

  setConversationId: (id) => {
    set({ conversationId: id })

    if (id) {
      const { conversations } = get()
      const existingIndex = conversations.findIndex((c) => c.id === id)

      if (existingIndex >= 0) {
        const updatedConversations = [...conversations]
        updatedConversations[existingIndex] = {
          ...updatedConversations[existingIndex],
          lastUpdated: Date.now(),
        }
        set({ conversations: updatedConversations })
      } else {
        const newConversation = {
          id,
          title: 'Conversation',
          lastUpdated: Date.now(),
        }
        set({ conversations: [...conversations, newConversation] })
      }
    }
  },

  addMessage: (message) => {
    const id = crypto.randomUUID()
    const timestamp = Date.now()
    const newMessage = { ...message, id, timestamp }

    set((state) => ({
      messages: [...state.messages, newMessage],
    }))

    if (get().conversationId) {
      const conversationId = get().conversationId
      const { conversations } = get()
      const existingIndex = conversations.findIndex((c) => c.id === conversationId)

      if (existingIndex >= 0) {
        const updatedConversations = [...conversations]
        updatedConversations[existingIndex] = {
          ...updatedConversations[existingIndex],
          lastUpdated: timestamp,
        }
        set({ conversations: updatedConversations })
      }
    }

    return id
  },

  updateLastMessage: (content, metadata = {}) => {
    const { messages } = get()
    if (messages.length === 0) return

    const lastIndex = messages.length - 1

    if (messages[lastIndex].role !== CHAT_ROLE.ASSISTANT) return

    const updatedMessages = [...messages]
    updatedMessages[lastIndex] = {
      ...updatedMessages[lastIndex],
      ...metadata,
      content,
    }

    set({ messages: updatedMessages })
  },

  clearMessages: () => set({ messages: [] }),

  loadConversation: (id, messages) => {
    if (!id) return

    // Log for debugging
    console.log('Loading conversation:', { id, messages })

    // Sort messages by timestamp if available
    const sortedMessages = [...messages].sort((a, b) => {
      // If timestamps are available, use them
      if (a.timestamp && b.timestamp) {
        return a.timestamp - b.timestamp
      }

      // Otherwise maintain the order they were passed in
      return 0
    })

    set({
      messages: sortedMessages,
      conversationId: id,
    })

    const { conversations } = get()
    const existingIndex = conversations.findIndex((c) => c.id === id)

    if (existingIndex >= 0) {
      const updatedConversations = [...conversations]
      updatedConversations[existingIndex] = {
        ...updatedConversations[existingIndex],
        lastUpdated: Date.now(),
      }
      set({ conversations: updatedConversations })
    }
  },

  updateConversationTitle: (id, title) => {
    const { conversations } = get()
    const conversationIndex = conversations.findIndex((c) => c.id === id)

    if (conversationIndex >= 0) {
      const updatedConversations = [...conversations]
      updatedConversations[conversationIndex] = {
        ...updatedConversations[conversationIndex],
        title,
        lastUpdated: Date.now(),
      }
      set({ conversations: updatedConversations })
    }
  },

  addConversation: (conversation) => {
    const { conversations } = get()
    const newConversation = {
      ...conversation,
      lastUpdated: Date.now(),
    }

    set({
      conversations: [...conversations, newConversation],
      conversationId: conversation.id,
    })
  },

  removeConversation: (id) => {
    const { conversations, conversationId } = get()
    const filteredConversations = conversations.filter((c) => c.id !== id)

    if (conversationId === id) {
      set({
        conversations: filteredConversations,
        messages: [],
        conversationId: null,
      })
    } else {
      set({ conversations: filteredConversations })
    }
  },

  clearAll: () =>
    set({
      messages: [],
      conversationId: null,
      conversations: [],
    }),
  errorCode: null,
  setErrorCode: (code) => set({ errorCode: code }),
}))

export default useChatStore
